import { ModalDrawer } from 'components/ModalDrawer/ModalDrawer'
import { snapBackwardToWeekEnd, snapForwardToWeekStart } from 'components/WeekRangePicker/weekRangeUtils'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { useEffect, useState } from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { apiFetch } from 'utils/apiFetch'
import { showNotification } from 'utils/showNotification'
import { getPermittedIrrigationReportProperties } from '../ReportForm/IrrigationReport/getPermittedIrrigationReportProperties'
import { IrrigationReport } from '../ReportForm/IrrigationReport/IrrigationReport'
import type { TEmailValidationError } from '../ReportForm/IrrigationReport/validateEmails'
import { splitEmails } from '../ReportForm/IrrigationReport/validateEmails'

const MIN_RANGE_DAYS = 4 * 7
const MAX_RANGE_DAYS = 52 * 7

const makePreviousYearRange = (timezone: string): [Date, Date] => {
  const previousYear = moment.tz(timezone).subtract(1, 'year')

  return [
    snapForwardToWeekStart(previousYear.clone().startOf('year')).toDate(),
    snapBackwardToWeekEnd(previousYear.clone().endOf('year')).toDate(),
  ]
}

export const OnDemandIrrigationReportModalDrawer = ({
  onClose,
  onCreated,
  opened,
}: {
  onClose: () => void
  onCreated: () => void
  opened: boolean
}) => {
  const defaultSelectedPropertyId = selectedFieldAssetsStore.useSelector((s) => s.property)
  const browserTimezone = moment.tz.guess()
  const [dates, setDates] = useState<[Date | null, Date | null]>(makePreviousYearRange(browserTimezone))
  const [error, setError] = useState<null | string>(null)
  const [loading, setLoading] = useState(false)
  const [emails, setEmails] = useState<string>('')
  const [emailErrors, setEmailErrors] = useState<TEmailValidationError | null>(null)
  const [selectedPropertyId, setSelectedPropertyId] = useState<null | number>(null)
  const dateFrom = dates[0] ? moment.tz(dates[0], browserTimezone).format('YYYY-MM-DD') : null
  const dateTo = dates[1] ? moment.tz(dates[1], browserTimezone).format('YYYY-MM-DD') : null

  useEffect(() => {
    if (opened) {
      setDates(makePreviousYearRange(browserTimezone))

      setError(null)

      const permittedProperties = getPermittedIrrigationReportProperties(fieldAssetStore.getState())

      setSelectedPropertyId((current) => {
        const isSelectable = (id: number | null) =>
          id !== null && permittedProperties.some((p) => p.propertyId === id)

        if (isSelectable(current)) return current

        return isSelectable(defaultSelectedPropertyId) ? defaultSelectedPropertyId : null
      })
    }
  }, [opened, browserTimezone, defaultSelectedPropertyId])

  const handleDateChange = (e: [Date | null, Date | null]) => {
    const [startDate, endDate] = e

    if (startDate && endDate) {
      const daysInRange =
        moment.tz(endDate, browserTimezone).diff(moment.tz(startDate, browserTimezone), 'days') + 1

      if (daysInRange > MAX_RANGE_DAYS) {
        setError(translate.phrases.banyanApp('The selected date range cannot exceed one year.'))
      } else if (daysInRange < MIN_RANGE_DAYS) {
        setError(
          translate.phrases.banyanApp('The selected date range must be at least one month (4 full weeks).'),
        )
      } else {
        setError(null)
      }
    } else {
      setError(null)
    }

    setDates([startDate, endDate])
  }

  return (
    <ModalDrawer
      title={translate.phrases.banyanApp('Create Irrigation Report')}
      primaryButtonDisabled={
        !dateFrom || !dateTo || error !== null || loading || emailErrors !== null || !selectedPropertyId
      }
      primaryButtonText={translate.phrases.banyanApp('Create Report')}
      primaryButtonOnPress={async () => {
        if (loading || !selectedPropertyId || !dateFrom || !dateTo) return

        setLoading(true)

        try {
          const response = await apiFetch({
            url: '/on-demand-irrigation-report-create',
            body: {
              dateFrom,
              dateTo,
              emails: splitEmails(emails),
              propertyId: selectedPropertyId,
            },
          })

          if (response.report) {
            showNotification({
              type: 'success',
              message: translate.phrases.banyanApp(
                'Report generation has started. You can check its status in the reports list.',
              ),
            })

            setEmails('')

            onCreated()

            onClose()
          } else if (response.errorCode === 'DUPLICATE_PENDING_REQUEST') {
            showNotification({
              type: 'error',
              message: translate.phrases.banyanApp(
                'A report for this property and date range is already being generated.',
              ),
            })
          } else if (response.errorCode === 'TOO_MANY_PENDING_REQUESTS') {
            showNotification({
              type: 'error',
              message: translate.phrases.banyanApp(
                'You have too many reports in progress. Please wait for them to finish before creating another.',
              ),
            })
          } else {
            showNotification({
              type: 'error',
              message: translate.phrases.banyanApp('Failed to create the report. Please try again.'),
            })
          }
        } catch (err) {
          showNotification({
            type: 'error',
            message: translate.phrases.banyanApp('Failed to create the report. Please try again.'),
          })
        } finally {
          setLoading(false)
        }
      }}
      opened={opened}
      onClose={onClose}
      zIndex={SharedSettings.DEFAULT_MODAL_DRAWER_Z_INDEX + 100}
    >
      <IrrigationReport
        dates={dates}
        error={error}
        loading={loading}
        emails={emails}
        setEmails={setEmails}
        emailErrors={emailErrors}
        setEmailErrors={setEmailErrors}
        selectedPropertyId={selectedPropertyId}
        setSelectedPropertyId={setSelectedPropertyId}
        handleDateChange={handleDateChange}
      />
    </ModalDrawer>
  )
}
